import { Injectable } from '@angular/core';
import { ValidityType } from '../shared/models/ticket';

export interface DateRange {
    startDate: Date;
    endDate: Date;
}

@Injectable({ providedIn: 'root' })
export class ValidityDateService {
    calculateRange(
        startDateObj: Date,
        validityType: ValidityType,
        duration: number,
        startTime: string,
        fixedEndTime: string
    ): DateRange | null {
        const start = new Date(startDateObj);

        switch (validityType) {
            case ValidityType.Minutes:
                return this.calcMinutes(start, startTime, duration);
            case ValidityType.TicketDays:
                return this.calcTicketDays(start, duration);
            case ValidityType.CalendarDays:
                return this.calcCalendarDays(start, duration);
            case ValidityType.SpecificTime:
                return this.calcSpecificTime(start, startTime, fixedEndTime);
            default:
                return null;
        }
    }

    formatTime(date: Date): string {
        return [date.getHours(), date.getMinutes()]
            .map(n => n.toString().padStart(2, '0'))
            .join(':');
    }

    formatDatetime(date: Date, locale: string = 'da-DK'): string {
        const datePart = new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }).format(date);

        const pad = (n: number) => n.toString().padStart(2, '0');
        const timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}`;

        return `${datePart} ${timePart}`;
    }

    applyTimeToDate(date: Date, time: string): Date {
        const [hours, minutes] = time.split(':').map(Number);
        const result = new Date(date);
        result.setHours(hours, minutes, 0, 0);
        return result;
    }

    private calcMinutes(start: Date, startTime: string, duration: number): DateRange | null {
        if (!startTime) return null;
        const startDate = this.applyTimeToDate(start, startTime);
        const endDate = new Date(startDate.getTime() + duration * 60000);
        return { startDate, endDate };
    }

    private calcTicketDays(start: Date, duration: number): DateRange {
        const endDate = new Date(start);
        endDate.setDate(endDate.getDate() + duration);
        endDate.setHours(3, 59, 0, 0);
        return { startDate: start, endDate };
    }

    private calcCalendarDays(start: Date, duration: number): DateRange {
        const endDate = new Date(start);
        endDate.setDate(endDate.getDate() + duration - 1);
        endDate.setHours(23, 59, 0, 0);
        return { startDate: start, endDate };
    }

    private calcSpecificTime(start: Date, startTime: string, fixedEndTime: string): DateRange {
        const startDate = this.applyTimeToDate(start, startTime);
        const endDate = this.applyTimeToDate(new Date(start), fixedEndTime);
        return { startDate, endDate };
    }
}